import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { Info, Plus } from "lucide-react";
import { useEffect, useState } from "react";

import type { AppRouter } from "@runcheetah/api/router";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from "@runcheetah/ui";
import { CodeText, PageHeader, QueryErrorState } from "@/components/common";
import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";

type RouterInputs = inferRouterInputs<AppRouter>;
type RouterOutputs = inferRouterOutputs<AppRouter>;
type BillingPlan = RouterOutputs["admin"]["billingPlans"]["list"][number];
type BillingPlanWriteInput = RouterInputs["admin"]["billingPlans"]["create"];

interface BillingPlanFormState extends BillingPlanWriteInput {
  id: string | null;
  monthlyPriceInput: string;
  yearlyPriceInput: string;
}

const NEW_PLAN_ID = "new";

const emptyPlanForm = (): BillingPlanFormState => ({
  id: null,
  code: "",
  name: "",
  source: "stripe",
  active: false,
  brandLimit: 1,
  monthlyCreditAllowance: 300,
  currency: "eur",
  monthlyPriceCents: null,
  yearlyPriceCents: null,
  monthlyPriceInput: "",
  yearlyPriceInput: "",
});

const planToForm = (plan: BillingPlan): BillingPlanFormState => ({
  id: plan.id,
  code: plan.code,
  name: plan.name,
  source: plan.source,
  active: plan.active,
  brandLimit: plan.brandLimit ?? 0,
  monthlyCreditAllowance: plan.monthlyCreditAllowance ?? 0,
  currency: plan.currency,
  monthlyPriceCents: plan.monthlyPriceCents,
  yearlyPriceCents: plan.yearlyPriceCents,
  monthlyPriceInput: formatEuroPriceInput(plan.monthlyPriceCents),
  yearlyPriceInput: formatEuroPriceInput(plan.yearlyPriceCents),
});

const parseIntegerInput = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : 0;
};

const parseEuroPriceInput = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed.length) {
    return null;
  }

  const parsed = Number(trimmed.replace(",", "."));
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 100)) : null;
};

const formatEuroPriceInput = (value: number | null): string =>
  value === null ? "" : (value / 100).toFixed(2);

function BillingPlanList({
  plans,
  selectedId,
  showActiveOnly,
  onToggleShowActiveOnly,
  onSelect,
}: {
  plans: BillingPlan[];
  selectedId: string;
  showActiveOnly: boolean;
  onToggleShowActiveOnly: (showActiveOnly: boolean) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">Plans</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onToggleShowActiveOnly(!showActiveOnly)}
          >
            {showActiveOnly ? "Show all" : "Show active"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {showActiveOnly
            ? "Showing active plans only."
            : "Showing all plans. Active plans stay first."}
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <button
          type="button"
          onClick={() => onSelect(NEW_PLAN_ID)}
          className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition hover:bg-muted ${
            selectedId === NEW_PLAN_ID ? "border-primary bg-primary/5" : "border-border"
          }`}
        >
          <span className="inline-flex items-center gap-2 font-medium">
            <Plus className="size-4" /> New plan
          </span>
        </button>

        {plans.length ? null : (
          <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
            No plans match this filter.
          </div>
        )}

        {plans.map((plan) => (
          <button
            key={plan.id}
            type="button"
            onClick={() => onSelect(plan.id)}
            className={`w-full rounded-lg border px-3 py-2 text-left transition hover:bg-muted ${
              selectedId === plan.id ? "border-primary bg-primary/5" : "border-border"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium">{plan.name}</span>
              <div className="flex items-center gap-1">
                <Badge variant="outline">v{plan.version}</Badge>
                <Badge variant={plan.source === "stripe" ? "default" : "outline"}>
                  {plan.source === "stripe" ? "Stripe" : "Manual"}
                </Badge>
                <Badge variant={plan.active ? "default" : "outline"}>
                  {plan.active ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
            <CodeText className="mt-1 block">{plan.code}</CodeText>
            <div className="mt-2 text-xs text-muted-foreground">
              {plan.brandLimit} brands · {plan.monthlyCreditAllowance} credits/mo
            </div>
            {plan.source === "stripe" ? (
              <div className="mt-1 text-xs text-muted-foreground">
                Sync: {plan.stripeSyncStatus.replace("_", " ")}
              </div>
            ) : null}
            <div className="mt-1 text-xs text-muted-foreground">
              {plan.brandCount} {plan.brandCount === 1 ? "brand uses" : "brands use"} this plan
            </div>
          </button>
        ))}
      </CardContent>
    </Card>
  );
}

function BillingPlanEditor({
  form,
  selectedPlan,
  wouldExceedActiveLimit,
  stripePlanMissingPrice,
  isPending,
  errorMessage,
  onChange,
  onSave,
  onDelete,
}: {
  form: BillingPlanFormState;
  selectedPlan: BillingPlan | null;
  wouldExceedActiveLimit: boolean;
  stripePlanMissingPrice: boolean;
  isPending: boolean;
  errorMessage: string | null;
  onChange: (form: BillingPlanFormState) => void;
  onSave: () => void;
  onDelete: () => void;
}) {
  const canDelete = selectedPlan ? selectedPlan.assignmentCount === 0 : false;

  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base">{form.id ? "Edit plan" : "Create plan"}</CardTitle>
          <Badge variant={form.active ? "default" : "outline"}>
            {form.active ? "Active" : "Inactive"}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Saving commercial changes creates a new immutable version. Stripe plans sync automatically
          unless marked as manual.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <span className="text-sm font-medium">Code</span>
            <Input
              value={form.code}
              disabled={isPending}
              onChange={(event) => onChange({ ...form, code: event.target.value })}
              placeholder="starter"
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Name</span>
            <Input
              value={form.name}
              disabled={isPending}
              onChange={(event) => onChange({ ...form, name: event.target.value })}
              placeholder="Starter"
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Brand limit</span>
            <Input
              type="number"
              min={0}
              value={form.brandLimit}
              disabled={isPending}
              onChange={(event) =>
                onChange({ ...form, brandLimit: parseIntegerInput(event.target.value) })
              }
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Monthly credit allowance</span>
            <Input
              type="number"
              min={0}
              value={form.monthlyCreditAllowance}
              disabled={isPending}
              onChange={(event) =>
                onChange({
                  ...form,
                  monthlyCreditAllowance: parseIntegerInput(event.target.value),
                })
              }
            />
          </label>
        </div>

        <label className="flex items-start gap-3 rounded-lg border p-3">
          <input
            type="checkbox"
            className="mt-1 size-4"
            checked={form.active}
            disabled={isPending}
            onChange={(event) => onChange({ ...form, active: event.target.checked })}
          />
          <span className="space-y-1">
            <span className="block text-sm font-medium">Active plan</span>
            <span className="block text-sm text-muted-foreground">
              Active plans are customer-facing. Maximum active plans: 2.
            </span>
          </span>
        </label>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <span className="flex items-center gap-2 text-sm font-medium">
              Monthly price (€)
              <span aria-label="Tax information" title="Stripe handles VAT/tax during checkout.">
                <Info className="size-4 text-muted-foreground" />
              </span>
            </span>
            <Input
              inputMode="decimal"
              value={form.monthlyPriceInput}
              disabled={isPending}
              onChange={(event) => {
                const value = event.target.value;
                onChange({
                  ...form,
                  monthlyPriceInput: value,
                  monthlyPriceCents: parseEuroPriceInput(value),
                });
              }}
              placeholder="29.00"
            />
          </label>
          <label className="space-y-2">
            <span className="flex items-center gap-2 text-sm font-medium">
              Yearly price (€)
              <span aria-label="Tax information" title="Stripe handles VAT/tax during checkout.">
                <Info className="size-4 text-muted-foreground" />
              </span>
            </span>
            <Input
              inputMode="decimal"
              value={form.yearlyPriceInput}
              disabled={isPending}
              onChange={(event) => {
                const value = event.target.value;
                onChange({
                  ...form,
                  yearlyPriceInput: value,
                  yearlyPriceCents: parseEuroPriceInput(value),
                });
              }}
              placeholder="290.00"
            />
          </label>
        </div>

        {form.source === "stripe" ? (
          <div className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
            Stripe products and EUR prices are created automatically when you save. Price or
            entitlement changes create a new plan version; existing subscribers stay on their
            current version.
          </div>
        ) : null}

        <label className="flex items-start gap-3 rounded-lg border p-3">
          <input
            type="checkbox"
            className="mt-1 size-4"
            checked={form.source === "manual"}
            disabled={isPending}
            onChange={(event) =>
              onChange({ ...form, source: event.target.checked ? "manual" : "stripe" })
            }
          />
          <span className="space-y-1">
            <span className="flex items-center gap-2 text-sm font-medium">
              Do not create this product in Stripe
              <span
                aria-label="Manual plan information"
                title="Use manual plans for comped or internal assignments, such as beta testers, friends, or special customer arrangements. Manual plans can be assigned by admins but are not sold through Stripe Checkout."
              >
                <Info className="size-4 text-muted-foreground" />
              </span>
            </span>
            <span className="block text-sm text-muted-foreground">
              Keep unchecked for paid customer plans that should sync to Stripe automatically.
            </span>
          </span>
        </label>

        {selectedPlan?.source === "stripe" ? (
          <div className="grid gap-3 rounded-lg border p-3 text-xs text-muted-foreground md:grid-cols-3">
            <div>
              <span className="block font-medium text-foreground">Product</span>
              <CodeText>{selectedPlan.stripeProductId ?? "Not synced"}</CodeText>
            </div>
            <div>
              <span className="block font-medium text-foreground">Monthly price</span>
              <CodeText>{selectedPlan.stripeMonthlyPriceId ?? "None"}</CodeText>
            </div>
            <div>
              <span className="block font-medium text-foreground">Yearly price</span>
              <CodeText>{selectedPlan.stripeYearlyPriceId ?? "None"}</CodeText>
            </div>
            {selectedPlan.stripeSyncError ? (
              <div className="text-destructive md:col-span-3">{selectedPlan.stripeSyncError}</div>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={onSave}
            disabled={
              isPending ||
              !form.code.trim() ||
              !form.name.trim() ||
              wouldExceedActiveLimit ||
              stripePlanMissingPrice
            }
          >
            {isPending ? "Saving..." : form.id ? "Create new version" : "Save plan"}
          </Button>
          {wouldExceedActiveLimit ? (
            <span className="text-sm text-destructive">
              Deactivate another plan before creating a new active plan.
            </span>
          ) : null}
          {stripePlanMissingPrice ? (
            <span className="text-sm text-destructive">
              Stripe plans require at least one monthly or yearly price.
            </span>
          ) : null}
          {errorMessage ? <span className="text-sm text-destructive">{errorMessage}</span> : null}
        </div>

        {selectedPlan ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
            <div className="space-y-1">
              <p className="text-sm font-medium">Delete plan</p>
              <p className="text-sm text-muted-foreground">
                {canDelete
                  ? "This plan has no workspace assignments and can be removed."
                  : `This plan is assigned to ${selectedPlan.assignmentCount} ${
                      selectedPlan.assignmentCount === 1 ? "workspace" : "workspaces"
                    } and cannot be deleted.`}
              </p>
            </div>
            {canDelete ? (
              <Button variant="destructive" disabled={isPending} onClick={onDelete}>
                Delete plan
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function BillingPlansPage() {
  const utils = trpc.useUtils();
  const { data, isPending, error } = trpc.admin.billingPlans.list.useQuery();
  const [selectedId, setSelectedId] = useState(NEW_PLAN_ID);
  const [form, setForm] = useState<BillingPlanFormState>(emptyPlanForm);
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const createPlan = trpc.admin.billingPlans.create.useMutation({
    onSuccess: async (plan) => {
      await utils.admin.billingPlans.list.invalidate();
      setSelectedId(plan.id);
      setForm(planToForm(plan));
    },
  });

  const updatePlan = trpc.admin.billingPlans.update.useMutation({
    onSuccess: async (plan) => {
      await utils.admin.billingPlans.list.invalidate();
      setForm(planToForm(plan));
    },
  });

  const deletePlan = trpc.admin.billingPlans.delete.useMutation({
    onSuccess: async () => {
      await utils.admin.billingPlans.list.invalidate();
      setSelectedId(NEW_PLAN_ID);
      setForm(emptyPlanForm());
    },
  });

  const plans = data ?? [];
  const sortedPlans = plans.toSorted((left, right) => {
    if (left.active !== right.active) {
      return left.active ? -1 : 1;
    }

    return left.name.localeCompare(right.name);
  });
  const visiblePlans = showActiveOnly ? sortedPlans.filter((plan) => plan.active) : sortedPlans;
  const selectedPlan = plans.find((plan) => plan.id === selectedId) ?? null;
  const activePlanCount = plans.filter((plan) => plan.active).length;
  const hasStartedPlanForm = Boolean(form.id || form.code.trim() || form.name.trim());
  const stripePlanMissingPrice =
    hasStartedPlanForm &&
    form.source === "stripe" &&
    form.monthlyPriceCents === null &&
    form.yearlyPriceCents === null;
  const nextActiveCount = plans.reduce(
    (count, plan) => {
      const active = plan.id === form.id ? form.active : plan.active;
      return count + (active ? 1 : 0);
    },
    form.id ? 0 : form.active ? 1 : 0,
  );
  const wouldExceedActiveLimit = nextActiveCount > 2;

  useEffect(() => {
    if (selectedId === NEW_PLAN_ID) {
      setForm(emptyPlanForm());
      return;
    }

    if (selectedPlan) {
      setForm(planToForm(selectedPlan));
    }
  }, [selectedId, selectedPlan]);

  if (isPending) {
    return (
      <div className="space-y-4">
        <PageHeader title="Billing Plans" meta="Loading..." />
        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <Card className="h-80 animate-pulse bg-muted" />
          <Card className="h-96 animate-pulse bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <QueryErrorState
        title="Failed to load billing plans"
        message={error.message}
        isForbidden={error.data?.code === "FORBIDDEN"}
      />
    );
  }

  const save = () => {
    const payload: BillingPlanWriteInput = {
      code: form.code.trim(),
      name: form.name.trim(),
      source: form.source,
      active: form.active,
      brandLimit: form.brandLimit,
      monthlyCreditAllowance: form.monthlyCreditAllowance,
      currency: form.currency,
      monthlyPriceCents: form.monthlyPriceCents,
      yearlyPriceCents: form.yearlyPriceCents,
    };

    if (form.id) {
      updatePlan.mutate({ id: form.id, ...payload });
      return;
    }

    createPlan.mutate(payload);
  };

  const deleteSelectedPlan = () => {
    if (!form.id) {
      return;
    }

    deletePlan.mutate({ id: form.id });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Billing Plans"
        meta={`${activePlanCount}/2 active`}
        description="Configure customer-facing subscription plans and default entitlements."
      />

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <BillingPlanList
          plans={visiblePlans}
          selectedId={selectedId}
          showActiveOnly={showActiveOnly}
          onToggleShowActiveOnly={setShowActiveOnly}
          onSelect={setSelectedId}
        />
        <BillingPlanEditor
          form={form}
          selectedPlan={selectedPlan}
          wouldExceedActiveLimit={wouldExceedActiveLimit}
          stripePlanMissingPrice={stripePlanMissingPrice}
          isPending={createPlan.isPending || updatePlan.isPending || deletePlan.isPending}
          errorMessage={
            createPlan.error?.message ??
            updatePlan.error?.message ??
            deletePlan.error?.message ??
            null
          }
          onChange={setForm}
          onSave={save}
          onDelete={deleteSelectedPlan}
        />
      </div>

      <div className="text-xs text-muted-foreground">
        Last updated: {plans[0] ? formatDateTime(plans[0].updatedAt) : "No plans yet"}
      </div>
    </div>
  );
}
